// Utility functions for conditional redirect logic

/**
 * Storage key for original referrer
 */
const ORIGINAL_REFERRER_KEY = 'dasha_original_referrer';

/**
 * Max age for stored referrer (30 minutes)
 */
const REFERRER_MAX_AGE = 30 * 60 * 1000;

/**
 * Stores the original referrer before auth redirect with timestamp
 * Only stores if no valid referrer data already exists
 */
export function storeOriginalReferrer(): void {
  // Check if there's already valid referrer data
  const storedData = localStorage.getItem(ORIGINAL_REFERRER_KEY);
  if (storedData) {
    try {
      const referrerData = JSON.parse(storedData);
      const age = Date.now() - referrerData.timestamp;
      
      // If existing data is still valid, don't overwrite it
      if (age < REFERRER_MAX_AGE) {
        return;
      }
    } catch (error) {
      // Clear corrupted data and continue to store new data
      localStorage.removeItem(ORIGINAL_REFERRER_KEY);
    }
  }

  // Store new referrer data only if none exists or existing data is expired
  const referrer = document.referrer;
  const referrerData = {
    url: referrer,
    timestamp: Date.now()
  };
  localStorage.setItem(ORIGINAL_REFERRER_KEY, JSON.stringify(referrerData));
}

/**
 * Gets the original referrer from storage or current document.referrer
 */
export function getOriginalReferrer(): string {
  // First try to get from local storage
  const storedData = localStorage.getItem(ORIGINAL_REFERRER_KEY);
  if (storedData) {
    try {
      const referrerData = JSON.parse(storedData);
      const age = Date.now() - referrerData.timestamp;
      
      // Check if referrer data is still valid (not older than 30 minutes)
      if (age < REFERRER_MAX_AGE) {
        return referrerData.url;
      } else {
        // Clear stale data
        localStorage.removeItem(ORIGINAL_REFERRER_KEY);
      }
    } catch (error) {
      // Clear corrupted data
      localStorage.removeItem(ORIGINAL_REFERRER_KEY);
    }
  }
  
  // Fallback to current referrer
  return document.referrer;
}

/**
 * Clears the stored original referrer (call this after successful redirect)
 */
export function clearOriginalReferrer(): void {
  localStorage.removeItem(ORIGINAL_REFERRER_KEY);
}

/**
 * Checks if the current location is the root page
 */
export function isRootPage(): boolean {
  const { pathname } = window.location;
  return pathname === '/' || pathname === '';
}

/**
 * Checks if the user came from dasha.ai or *.dasha.ai domains
 * but not from selectServers().auth
 */
export function isFromDashaDomainButNotAuth(authServer: string, blackboxServer: string): boolean {
  const referrer = getOriginalReferrer();
  if (!referrer) return false;
  
  try {
    const referrerUrl = new URL(referrer);
    const referrerHostname = referrerUrl.hostname;
    
    // Check if it's from dasha.ai or *.dasha.ai
    const isDashaDomain = referrerHostname === 'dasha.ai' || referrerHostname.endsWith('.dasha.ai');
    
    // Check if it's NOT from auth server
    const authUrl = new URL(authServer.startsWith('http') ? authServer : `https://${authServer}`);
    const blackboxUrl = new URL(blackboxServer.startsWith('http') ? blackboxServer : `https://${blackboxServer}`);
    const isFromAuth = referrerHostname === authUrl.hostname;
    const isFromBlackbox = referrerHostname === blackboxUrl.hostname;
    return isDashaDomain && !isFromAuth && !isFromBlackbox;
  } catch (error) {
    return false;
  }
}

/**
 * Checks if the user came from Google or other search engines
 */
export function isFromSearchEngine(): boolean {
  const referrer = getOriginalReferrer();
  if (!referrer) return false;
  
  try {
    const referrerUrl = new URL(referrer);
    const hostname = referrerUrl.hostname;
    
    const searchEngines = [
      'google.com',
      'google.co.uk',
      'google.ca',
      'google.com.au',
      'google.de',
      'google.fr',
      'google.es',
      'google.it',
      'google.co.jp',
      'google.co.kr',
      'google.com.br',
      'google.ru',
      'bing.com',
      'yahoo.com',
      'duckduckgo.com',
      'yandex.com',
      'yandex.ru',
      'baidu.com',
      'search.yahoo.com',
      'startpage.com'
    ];
    
    return searchEngines.some(engine => 
      hostname === engine || hostname.endsWith(`.${engine}`)
    );
  } catch (error) {
    return false;
  }
}

/**
 * Simple hash function for domain verification
 */
function simpleHash(str: string): string {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash; // Convert to 32-bit integer
  }
  return Math.abs(hash).toString(16);
}

/**
 * Checks if the user's email domain is in the forbidden list
 * Uses hash verification to obfuscate the actual forbidden domains
 */
export function isEmailDomainForbidden(email: string): boolean {
  if (!email) return false;
  
  const forbiddenDomainHashes = [
    '7ad9e282', 
    '6955a767'  
  ];
  
  const emailDomain = email.split('@')[1]?.toLowerCase();
  if (!emailDomain) return false;
  
  const domainHash = simpleHash(emailDomain);
  
  return forbiddenDomainHashes.includes(domainHash);
}

/**
 * Determines if redirect should happen based on all conditions
 */
export function shouldRedirect(userEmail: string, authServer: string, blackboxServer: string): boolean {
  const onRootPage = isRootPage();
  const emailForbidden = isEmailDomainForbidden(userEmail);
  const fromDashaButNotAuth = isFromDashaDomainButNotAuth(authServer, blackboxServer);
  const fromSearchEngine = isFromSearchEngine();
  
  // Must be on root page
  if (!onRootPage) {
    return false;
  }
  
  // Must NOT be from forbidden email domain
  if (emailForbidden) {
    return false;
  }
  
  // Must be from dasha.ai (but not auth) OR from search engine
  return fromDashaButNotAuth || fromSearchEngine;
}
